import { useEffect, useMemo, useState } from 'react';
import { EmployeesApi, type EmployeeOption, type EmployeeInput } from '../lib/crmApi';
import { useToast } from '../components/Toast';

const ROLES: Array<{ value: EmployeeInput['role']; label: string }> = [
  { value: 'admin', label: 'Admin (acesso total)' },
  { value: 'coordinator', label: 'Coordenadora' },
  { value: 'attendant', label: 'Atendente' },
  { value: 'viewer', label: 'Viewer (somente leitura)' },
];

const EMPTY: EmployeeInput = {
  name: '',
  role: 'attendant',
  whu_user_id: '',
  extension_3cx: '',
  is_active: true,
};

export default function AdminEmployees() {
  const toast = useToast();
  const [rows, setRows] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeOption | null>(null);
  const [form, setForm] = useState<EmployeeInput>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => { reload(); }, []);

  async function reload() {
    setLoading(true);
    const res = await EmployeesApi.list();
    if (!res.ok) toast.error(`Falha ao carregar: ${res.error ?? 'erro'}`);
    setRows(res.ok ? res.data?.data ?? [] : []);
    setLoading(false);
  }

  const visible = useMemo(
    () => (showInactive ? rows : rows.filter((r) => r.is_active)),
    [rows, showInactive],
  );

  const isOnline = (ts?: string | null) =>
    !!ts && Date.now() - new Date(ts).getTime() < 2 * 60 * 1000;

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setFormOpen(true);
  }
  function openEdit(e: EmployeeOption) {
    setEditing(e);
    setForm({
      name: e.name,
      role: (e.role as EmployeeInput['role']) ?? 'attendant',
      whu_user_id: e.whu_user_id ?? '',
      extension_3cx: e.extension_3cx ?? '',
      is_active: e.is_active,
    });
    setFormOpen(true);
  }

  async function submitForm() {
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    const payload: EmployeeInput = {
      ...form,
      whu_user_id: form.whu_user_id?.trim() || null,
      extension_3cx: form.extension_3cx?.trim() || null,
    };
    const res = editing
      ? await EmployeesApi.update(editing.id, payload)
      : await EmployeesApi.create(payload);
    setSaving(false);
    if (!res.ok) {
      toast.error(`Falha ao salvar: ${res.error ?? 'erro'}`);
      return;
    }
    toast.success(editing ? 'Funcionária atualizada' : 'Funcionária cadastrada');
    setFormOpen(false);
    reload();
  }

  async function toggleActive(e: EmployeeOption) {
    const res = await EmployeesApi.update(e.id, { is_active: !e.is_active });
    if (!res.ok) { toast.error(`Falha: ${res.error ?? 'erro'}`); return; }
    toast.success(e.is_active ? 'Desativada' : 'Ativada');
    setRows((prev) => prev.map((r) => (r.id === e.id ? { ...r, is_active: !r.is_active } : r)));
  }

  async function deactivate(e: EmployeeOption) {
    if (!confirm(`Desativar "${e.name}"? (soft delete, reativa depois se precisar)`)) return;
    const res = await EmployeesApi.deactivate(e.id);
    if (!res.ok) { toast.error(`Falha: ${res.error ?? 'erro'}`); return; }
    toast.success('Desativada');
    setRows((prev) => prev.map((r) => (r.id === e.id ? { ...r, is_active: false } : r)));
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Funcionárias</h1>
          <p className="text-sm text-slate-500">
            Cadastro, roles, vínculo WHU e ramal 3CX. Total: {rows.length} ({rows.filter(r => r.is_active).length} ativas)
          </p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Mostrar inativas
          </label>
          <button
            onClick={openCreate}
            className="bg-emerald-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-emerald-700"
          >
            + Nova funcionária
          </button>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <p className="p-5 text-sm text-slate-500">Carregando…</p>
        ) : visible.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">Nenhuma funcionária cadastrada.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-slate-500 border-b bg-slate-50">
              <tr>
                <th className="py-2 pl-4">Nome</th>
                <th>Role</th>
                <th>WHU userId</th>
                <th>Ramal 3CX</th>
                <th>Online</th>
                <th>Último ping</th>
                <th>Status</th>
                <th className="pr-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <tr key={e.id} className={`border-b last:border-0 hover:bg-slate-50 ${!e.is_active ? 'opacity-50' : ''}`}>
                  <td className="py-2.5 pl-4 font-medium">{e.name}</td>
                  <td>
                    <RoleBadge role={e.role} />
                  </td>
                  <td className="text-xs font-mono text-slate-500">
                    {e.whu_user_id ?? <span className="text-slate-300">—</span>}
                  </td>
                  <td className="text-xs font-mono">
                    {e.extension_3cx ?? <span className="text-slate-300">—</span>}
                  </td>
                  <td>
                    {isOnline(e.last_ping_at) ? (
                      <span className="text-emerald-600 text-lg leading-none">●</span>
                    ) : (
                      <span className="text-slate-300 text-lg leading-none">●</span>
                    )}
                  </td>
                  <td className="text-xs text-slate-500">
                    {e.last_ping_at ? new Date(e.last_ping_at).toLocaleString('pt-BR') : '—'}
                  </td>
                  <td>
                    <button
                      onClick={() => toggleActive(e)}
                      className={`text-xs px-2 py-0.5 rounded ${
                        e.is_active
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      {e.is_active ? 'ativa' : 'inativa'}
                    </button>
                  </td>
                  <td className="pr-4 text-right space-x-2">
                    <button
                      onClick={() => openEdit(e)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      editar
                    </button>
                    {e.is_active && (
                      <button
                        onClick={() => deactivate(e)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        desativar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {formOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => !saving && setFormOpen(false)}
        >
          <div
            className="bg-white rounded-lg w-full max-w-lg p-5 space-y-4"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">
              {editing ? `Editar: ${editing.name}` : 'Nova funcionária'}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 block mb-1">Nome completo *</label>
                <input
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Caroline Silva"
                />
              </div>

              <div>
                <label className="text-xs text-slate-600 block mb-1">Role *</label>
                <select
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value as EmployeeInput['role'] })}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 block mb-1">
                    WHU userId <span className="text-slate-400">(opcional)</span>
                  </label>
                  <input
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
                    value={form.whu_user_id ?? ''}
                    onChange={(e) => setForm({ ...form, whu_user_id: e.target.value })}
                    placeholder="64e38c24..."
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-600 block mb-1">
                    Ramal 3CX <span className="text-slate-400">(opcional)</span>
                  </label>
                  <input
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
                    value={form.extension_3cx ?? ''}
                    onChange={(e) => setForm({ ...form, extension_3cx: e.target.value })}
                    placeholder="101"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="emp-active"
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <label htmlFor="emp-active" className="text-sm">Ativa</label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <button
                onClick={() => setFormOpen(false)}
                disabled={saving}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
              >
                Cancelar
              </button>
              <button
                onClick={submitForm}
                disabled={saving}
                className="bg-emerald-600 text-white px-4 py-1.5 rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Salvando…' : editing ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const map: Record<string, string> = {
    admin: 'bg-violet-100 text-violet-700',
    coordinator: 'bg-blue-100 text-blue-700',
    attendant: 'bg-slate-100 text-slate-700',
    viewer: 'bg-amber-100 text-amber-700',
  };
  const cls = map[role] ?? 'bg-slate-100 text-slate-700';
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{role}</span>;
}
